import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatBytes, formatDuration } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { useMediaForDance, useDeleteMedia } from '@/data/media';
import { MediaUploader } from '@/components/media/MediaUploader';
import { VideoPlayer } from '@/components/media/VideoPlayer';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';

export const Route = createFileRoute('/admin/dances/$danceId/media')({
  component: AdminDanceMedia,
});

function useDanceContext(danceId: string) {
  return useQuery({
    queryKey: ['dance', danceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dances')
        .select('id, name, productions:production_id(id, name, season)')
        .eq('id', danceId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function AdminDanceMedia() {
  const { danceId } = Route.useParams();
  const dance = useDanceContext(danceId);
  const media = useMediaForDance(danceId);
  const del = useDeleteMedia();

  return (
    <div className="space-y-10">
      <header>
        {dance.data?.productions && (
          <Link
            to="/admin/productions/$id"
            params={{ id: dance.data.productions.id }}
            className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
          >
            ← {dance.data.productions.name}
          </Link>
        )}
        <h1 className="mt-2">{dance.data?.name ?? 'Dance'} — media</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Upload choreography videos or photos. Only families with a dancer cast in this dance will
          see the media in their parent portal — the RLS policies enforce cast-access at the row
          level.
        </p>
      </header>

      <MediaUploader danceId={danceId} defaultKind="video" />

      <section aria-labelledby="existing-heading">
        <h2 id="existing-heading" className="text-[var(--text-xl)]">
          Existing media
        </h2>
        {media.isLoading && <p aria-live="polite">Loading…</p>}
        {!media.isLoading && (media.data?.length ?? 0) === 0 && (
          <Empty title="No media yet" description="Upload a video above to get started." />
        )}
        <ul className="mt-4 grid gap-4">
          {(media.data ?? []).map((m) => (
            <li key={m.id} className="surface-elevated grid gap-3 p-4">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <p className="font-medium">{m.title ?? '(untitled)'}</p>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    <Badge tone="neutral">{m.kind}</Badge>{' '}
                    <Badge
                      tone={
                        m.upload_status === 'complete'
                          ? 'success'
                          : m.upload_status === 'failed'
                            ? 'danger'
                            : 'warn'
                      }
                    >
                      {m.upload_status}
                    </Badge>{' '}
                    {formatBytes(m.size_bytes)}
                    {m.duration_seconds != null && <> · {formatDuration(m.duration_seconds)}</>}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => del.mutate({ id: m.id })}
                  disabled={del.isPending}
                >
                  Delete
                </Button>
              </div>
              {m.upload_status === 'complete' && m.kind === 'video' && (
                <VideoPlayer assetId={m.id} watermark="admin preview" />
              )}
              {m.description && (
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  {m.description}
                </p>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
